import { TBorrowerType } from '../../BorrowerStep/BorrowerStep';
import {
  TSubscriber,
  ESubscriberType,
} from '../../SubscribersStep/SubscribersAddNewSideSheet';
import { TPreviewSimulationData } from 'app/components/CreditSimulator/CreditSimulatorModal/CreditSimulatorContent';
import { IConsigned } from '../../ConsignedStep/interfaces';
import { TBeneficiaryAccount } from 'modules/products/interfaces/beneficiaryAccount';

/**
 * Converte TBorrowerType ('PERSON' | 'BUSINESS') para o formato usado pelos componentes
 * ('PERSON' | 'COMPANY'). Mapeia 'BUSINESS' para 'COMPANY'.
 */
export const parseBorrowerType = (
  borrowerType: TBorrowerType,
): 'PERSON' | 'COMPANY' => {
  return borrowerType === 'BUSINESS' ? 'COMPANY' : borrowerType;
};

/**
 * Interface local para Subscriber usado no componente ReviewStepSubscribers
 */
export interface ParsedSubscriber {
  subscriber: {
    full_name?: string;
    taxpayer_id?: string;
    phone?: {
      area_code?: string;
      number?: string;
    };
    email_address?: string;
  };
  subscriberType: ESubscriberType;
  subscriberPersonType: 'PERSON' | 'COMPANY';
}

/**
 * Converte TSubscriber[] para o formato usado pelo componente ReviewStepSubscribers.
 * Mapeia subscriberPersonType de 'BUSINESS' para 'COMPANY'.
 */
export const parseSubscribers = (
  subscribers: TSubscriber[] | null,
): ParsedSubscriber[] | null => {
  if (!subscribers) {
    return null;
  }

  return subscribers.map((subscriber) => ({
    ...subscriber,
    subscriberPersonType: parseBorrowerType(subscriber.subscriberPersonType),
  }));
};

/**
 * Interface FinalSimulation usada no componente ReviewStepInstallmentsAccordion
 */
export interface FinalSimulation {
  schedule?: Array<{
    payment: number;
    principal: number;
    interest: number;
    iof: number;
    payment_date: string;
  }>;
  disbursement_date?: string;
}

/**
 * Converte TPreviewSimulationData para FinalSimulation.
 * Converte disbursement_date de string | null para string | undefined.
 */
export const parseFinalSimulation = (
  simulation: TPreviewSimulationData | null,
): FinalSimulation | null => {
  if (!simulation) {
    return null;
  }

  return {
    schedule: simulation.schedule,
    disbursement_date:
      simulation.disbursement_date === null
        ? undefined
        : simulation.disbursement_date,
  };
};

/**
 * Interface ConsignedConfig usada no componente ReviewStepConsignedSiape
 */
export interface ConsignedConfig {
  employer?: {
    name?: string;
    code?: string;
  };
  employee?: {
    code?: string;
  };
  meta_data?: {
    classification_description?: string;
    functional_status_description?: string;
  };
}

/**
 * Converte IConsigned | undefined para ConsignedConfig | null.
 * Converte undefined para null e mapeia os campos necessários.
 */
export const parseConsignedConfig = (
  consigned: IConsigned | undefined,
): ConsignedConfig | null => {
  if (!consigned) {
    return null;
  }

  return {
    employer: consigned.employer
      ? {
          name: consigned.employer.name,
          code: consigned.employer.code?.toString(),
        }
      : undefined,
    employee: consigned.employee
      ? {
          code: consigned.employee.code?.toString(),
        }
      : undefined,
    meta_data: consigned.meta_data
      ? {
          classification_description:
            consigned.meta_data.classification_description,
          functional_status_description:
            consigned.meta_data.functional_status_description,
        }
      : undefined,
  };
};

/**
 * Interface BeneficiaryAccount usada no componente ReviewStepBeneficiaryAccount
 */
export interface BeneficiaryAccount {
  description: string;
  holder: {
    name: string;
    taxpayer_id: string;
  };
  pix?: {
    key: string;
    key_type: string;
  };
  external_bank_account?: {
    ispb_code?: string;
    bank_account_type?: string;
    bank_branch?: string;
    bank_account?: string;
    bank_account_digit?: string;
  };
}

/**
 * Converte TBeneficiaryAccount para BeneficiaryAccount.
 * Converte pix de { key: string; key_type: string; } | null para { key: string; key_type: string; } | undefined.
 */
export const parseBeneficiaryAccount = (
  account: TBeneficiaryAccount,
): BeneficiaryAccount => {
  return {
    description: account.description,
    holder: account.holder,
    pix: account.pix === null ? undefined : account.pix,
    external_bank_account:
      account.external_bank_account === null
        ? undefined
        : account.external_bank_account
        ? {
            ispb_code: account.external_bank_account.ispb_code,
            bank_account_type: account.external_bank_account.bank_account_type,
            bank_branch: account.external_bank_account.bank_branch,
            bank_account: account.external_bank_account.bank_account,
            bank_account_digit:
              account.external_bank_account.bank_account_digit,
          }
        : undefined,
  };
};

/**
 * Interface Simulation usada no componente ReviewStepInstallmentsAccordion
 */
export interface Simulation {
  disbursement_date?: string;
}

/**
 * Converte TPreviewSimulationData | null para Simulation | null.
 * Converte disbursement_date de string | null para string | undefined.
 */
export const parseSimulation = (
  simulation: TPreviewSimulationData | null,
): Simulation | null => {
  if (!simulation) {
    return null;
  }

  return {
    disbursement_date:
      simulation.disbursement_date === null
        ? undefined
        : simulation.disbursement_date,
  };
};

/**
 * Interface SimulationOld usada no componente ReviewStepSimulationOld
 */
export interface SimulationOld {
  numPeriods?: number;
  paymentAmount?: number;
  requested_amount?: number;
  financed_amount?: number;
  interestRate?: number;
  monthlyEffectiveInterestRate?: number;
  tacAmount?: number;
  iofAmount?: number;
  insurance_amount?: number;
  firstPaymentDate?: string;
  lastPaymentDate?: string;
  totalAmountOwed?: number;
}

/**
 * Converte TPreviewSimulationData | null para SimulationOld | null.
 * Converte campos de null para undefined.
 */
export const parseSimulationOld = (
  simulation: TPreviewSimulationData | null,
): SimulationOld | null => {
  if (!simulation) {
    return null;
  }

  return {
    numPeriods:
      simulation.numPeriods === null ? undefined : simulation.numPeriods,
    paymentAmount:
      simulation.paymentAmount === null ? undefined : simulation.paymentAmount,
    requested_amount:
      simulation.requested_amount === null
        ? undefined
        : simulation.requested_amount,
    financed_amount:
      simulation.financed_amount === null
        ? undefined
        : simulation.financed_amount,
    interestRate:
      simulation.interestRate === null ? undefined : simulation.interestRate,
    monthlyEffectiveInterestRate:
      simulation.monthlyEffectiveInterestRate === null
        ? undefined
        : simulation.monthlyEffectiveInterestRate,
    tacAmount: simulation.tacAmount === null ? undefined : simulation.tacAmount,
    iofAmount: simulation.iofAmount === null ? undefined : simulation.iofAmount,
    insurance_amount:
      simulation.insurance_amount === null
        ? undefined
        : simulation.insurance_amount,
    firstPaymentDate:
      simulation.firstPaymentDate === null
        ? undefined
        : simulation.firstPaymentDate,
    lastPaymentDate:
      simulation.lastPaymentDate === null
        ? undefined
        : simulation.lastPaymentDate,
    totalAmountOwed:
      simulation.totalAmountOwed === null
        ? undefined
        : simulation.totalAmountOwed,
  };
};
